import { Flash } from '@carbon/icons-react';
import * as React from 'react';
import { NavbarDropdownItem, NavbarSchema } from 'vet-bones/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import { NAVBAR_PORTAL_ENDPOINT_BUTTON } from 'src/ui/constants/testIds';
import { NavbarAsideType, toggledNavbarAside } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useSavedConnectionsQuery } from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import { usePortalNavbarAvatarSchema } from 'src/ui/hooks/navbar/avatar';
import {
  useCurrentConnectionIdx,
  useLandingConnectionIdx,
  useOrgDomain,
} from 'src/ui/hooks/navbar/connection';
import {
  useKnowledgeCatalogNavbarDropdownItem,
  usePortalNavbarDropdownItem,
  useVoiceboxNavbarDropdownItem,
} from 'src/ui/hooks/navbar/dropdown';
import { useNavbarExpandSchema } from 'src/ui/hooks/navbar/expand';
import { useNavbarLearnStardogSchema } from 'src/ui/hooks/navbar/learnStardog';
import { useNavbarOrgDashboardSchema } from 'src/ui/hooks/navbar/orgDashboard';
import { useNavbarSupport } from 'src/ui/hooks/navbar/support';
import { useVoiceboxNavbarItems } from 'src/ui/hooks/navbar/voicebox';
import * as copy from 'src/ui/templates/copy';

export const usePortalLandingNavbarSchema = (): NavbarSchema => {
  const dispatch = useAppDispatch();

  const org = useOrgDomain();
  const connectionIdx = useLandingConnectionIdx();
  const dropdownSelectedItem = usePortalNavbarDropdownItem(connectionIdx);
  const voiceboxItem = useVoiceboxNavbarDropdownItem(connectionIdx);
  const knowledgeCatalogItem = useKnowledgeCatalogNavbarDropdownItem(
    connectionIdx
  );
  const { isKnowledgeCatalogSupported, isVoiceboxSupported } = useNavbarSupport(
    connectionIdx
  );
  const { connection: connectionFallback } = useCurrentConnection(
    connectionIdx,
    org
  );

  const { data: savedConnectionsData } = useSavedConnectionsQuery(
    graphQLClient,
    { org_domain: org }
  );
  const hasEndpoints = savedConnectionsData?.listConnections?.length;

  const connection =
    savedConnectionsData?.listConnections?.find(
      (connection) => connection?.index.toString() === connectionIdx
    ) ||
    // fallback to direct call to include connections not found in list (like the demo connection)
    connectionFallback;

  const dropdownItems = React.useMemo(() => {
    const items: NavbarDropdownItem[] = [];
    if (isVoiceboxSupported) {
      items.push(voiceboxItem);
    }
    if (isKnowledgeCatalogSupported) {
      items.push(knowledgeCatalogItem);
    }
    return items;
  }, [
    isKnowledgeCatalogSupported,
    isVoiceboxSupported,
    knowledgeCatalogItem,
    voiceboxItem,
  ]);

  const isEndpointAsideOpen = useAppSelector(
    (state) =>
      state.ui.navbar.aside === NavbarAsideType.PORTAL_ENDPOINT &&
      state.ui.navbar.isAsideOpen
  );
  const handlePortalEndpointAside = React.useCallback(() => {
    dispatch(
      toggledNavbarAside({
        aside: NavbarAsideType.PORTAL_ENDPOINT,
        isAsideOpen: !isEndpointAsideOpen,
      })
    );
  }, [dispatch, isEndpointAsideOpen]);

  const expand = useNavbarExpandSchema();
  const learnStardog = useNavbarLearnStardogSchema();
  const orgDashboard = useNavbarOrgDashboardSchema();
  const avatar = usePortalNavbarAvatarSchema();

  return React.useMemo(
    () => ({
      dropdown: {
        items: dropdownItems,
        selectedItem: dropdownSelectedItem,
      },
      expand,
      secondaryItems: hasEndpoints
        ? [
            {
              active: isEndpointAsideOpen,
              icon: Flash,
              label:
                connection?.name || copy.components.navbar.landing.endpoint,
              onClick: handlePortalEndpointAside,
              testId: NAVBAR_PORTAL_ENDPOINT_BUTTON,
            },
          ]
        : [],
      tertiaryItems: [learnStardog, ...orgDashboard],
      footer: { avatar },
    }),
    [
      avatar,
      connection,
      dropdownItems,
      dropdownSelectedItem,
      expand,
      handlePortalEndpointAside,
      hasEndpoints,
      isEndpointAsideOpen,
      learnStardog,
      orgDashboard,
    ]
  );
};

export const usePortalDefaultNavbarSchema = (): NavbarSchema => {
  const connectionIdx = useCurrentConnectionIdx();
  const dropdownSelectedItem = usePortalNavbarDropdownItem(connectionIdx);

  const expand = useNavbarExpandSchema();
  const learnStardog = useNavbarLearnStardogSchema();
  const orgDashboard = useNavbarOrgDashboardSchema();
  const avatar = usePortalNavbarAvatarSchema();

  return React.useMemo(
    () => ({
      dropdown: {
        items: [],
        selectedItem: dropdownSelectedItem,
      },
      expand,
      tertiaryItems: [learnStardog, ...orgDashboard],
      footer: { avatar },
    }),
    [avatar, dropdownSelectedItem, expand, learnStardog, orgDashboard]
  );
};

export const usePortalVoiceboxNavbarSchema = (): NavbarSchema => {
  const connectionIdx = useCurrentConnectionIdx();
  const dropdownSelectedItem = useVoiceboxNavbarDropdownItem(connectionIdx);
  const portalItem = usePortalNavbarDropdownItem(connectionIdx);
  const knowledgeCatalogItem = useKnowledgeCatalogNavbarDropdownItem(
    connectionIdx
  );
  const { isKnowledgeCatalogSupported } = useNavbarSupport(connectionIdx);

  const dropdownItems = React.useMemo(() => {
    const items: NavbarDropdownItem[] = [portalItem];
    if (isKnowledgeCatalogSupported) {
      items.push(knowledgeCatalogItem);
    }
    return items;
  }, [isKnowledgeCatalogSupported, knowledgeCatalogItem, portalItem]);

  const secondaryItems = useVoiceboxNavbarItems();

  const expand = useNavbarExpandSchema();
  const learnStardog = useNavbarLearnStardogSchema();
  const orgDashboard = useNavbarOrgDashboardSchema();
  const avatar = usePortalNavbarAvatarSchema();

  return React.useMemo(
    () => ({
      dropdown: {
        items: dropdownItems,
        selectedItem: dropdownSelectedItem,
      },
      expand,
      secondaryItems,
      tertiaryItems: [learnStardog, ...orgDashboard],
      footer: { avatar },
    }),
    [
      avatar,
      dropdownItems,
      dropdownSelectedItem,
      expand,
      learnStardog,
      orgDashboard,
      secondaryItems,
    ]
  );
};
